import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import AdminHeader from "../components/Admin/AdminHeader";
import "./AdminActivityEditor.css";

const AdminActivityEditor = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const isEditMode = Boolean(id);

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);

    const [selectedCoverImage, setSelectedCoverImage] = useState(null);
    const [coverPreview, setCoverPreview] = useState("");

    const [selectedGalleryImages, setSelectedGalleryImages] = useState([]);
    const [galleryPreviews, setGalleryPreviews] = useState([]);

    const [originalCoverImage, setOriginalCoverImage] = useState("");
    const [originalGalleryImages, setOriginalGalleryImages] = useState([]);

    const [form, setForm] = useState({
        title: "",
        slug: "",
        category: "",
        activity_date: "",
        location: "",
        description: "",
        cover_image: "",
        gallery_images: [],
        published: false,
        featured: false,
    });

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    /* =========================================================
       LOAD ACTIVITY
    ========================================================= */

    useEffect(() => {
        if (!isEditMode) {
            setLoading(false);
            return;
        }

        const loadActivity = async () => {
            setLoading(true);
            setError("");

            const { data, error: fetchError } = await supabase
                .from("activities")
                .select(`
          id,
          title,
          slug,
          category,
          activity_date,
          location,
          description,
          cover_image,
          gallery_images,
          published,
          featured
        `)
                .eq("id", id)
                .single();

            if (fetchError) {
                console.error("Error loading activity:", fetchError);
                setError("Unable to load activity.");
                setLoading(false);
                return;
            }

            const galleryImages = Array.isArray(data.gallery_images)
                ? data.gallery_images
                : [];

            setForm({
                title: data.title || "",
                slug: data.slug || "",
                category: data.category || "",
                activity_date: data.activity_date || "",
                location: data.location || "",
                description: data.description || "",
                cover_image: data.cover_image || "",
                gallery_images: galleryImages,
                published: Boolean(data.published),
                featured: Boolean(data.featured),
            });

            setOriginalCoverImage(data.cover_image || "");
            setOriginalGalleryImages(galleryImages);

            if (data.cover_image) {
                setCoverPreview(data.cover_image);
            }

            setGalleryPreviews(galleryImages);

            setLoading(false);
        };

        loadActivity();
    }, [id, isEditMode]);

    /* =========================================================
       FORM HANDLERS
    ========================================================= */

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        setForm((previous) => ({
            ...previous,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, "")
            .replace(/\s+/g, "-")
            .replace(/-+/g, "-");
    };

    const handleTitleChange = (event) => {
        const title = event.target.value;

        setForm((previous) => ({
            ...previous,
            title,
            slug:
                !isEditMode || previous.slug === generateSlug(previous.title)
                    ? generateSlug(title)
                    : previous.slug,
        }));
    };

    /* =========================================================
       FILE HELPERS
    ========================================================= */

    const getFileExtension = (file) => {
        const extension = file.name.split(".").pop();

        if (!extension) {
            return "jpg";
        }

        return extension.toLowerCase();
    };

    const createUniqueFileName = (file) => {
        const extension = getFileExtension(file);

        return `${Date.now()}-${Math.random()
            .toString(36)
            .substring(2, 10)}.${extension}`;
    };

    const getStoragePathFromUrl = (url) => {
        if (!url) return null;

        const marker =
            "/storage/v1/object/public/activities/";

        const markerIndex = url.indexOf(marker);

        if (markerIndex === -1) {
            return null;
        }

        return decodeURIComponent(
            url.substring(markerIndex + marker.length)
        );
    };

    /* =========================================================
       COVER IMAGE
    ========================================================= */

    const handleCoverImageChange = (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        if (!file.type.startsWith("image/")) {
            setError("Please select a valid image file.");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError("Cover image must be smaller than 5 MB.");
            return;
        }

        setError("");

        setSelectedCoverImage(file);

        const previewUrl = URL.createObjectURL(file);
        setCoverPreview(previewUrl);
    };

    const handleRemoveCoverImage = () => {
        setSelectedCoverImage(null);
        setCoverPreview("");

        setForm((previous) => ({
            ...previous,
            cover_image: "",
        }));
    };

    /* =========================================================
       GALLERY IMAGES
    ========================================================= */

    const handleGalleryImageChange = (event) => {
        const files = Array.from(event.target.files || []);

        if (files.length === 0) {
            return;
        }

        const invalidFile = files.find(
            (file) =>
                !file.type.startsWith("image/") ||
                file.size > 5 * 1024 * 1024
        );

        if (invalidFile) {
            setError(
                "Every gallery image must be an image smaller than 5 MB."
            );
            return;
        }

        setError("");

        const newPreviewUrls = files.map((file) =>
            URL.createObjectURL(file)
        );

        setSelectedGalleryImages((previous) => [
            ...previous,
            ...files,
        ]);

        setGalleryPreviews((previous) => [
            ...previous,
            ...newPreviewUrls,
        ]);
    };

    const handleRemoveGalleryImage = (index) => {
        setSelectedGalleryImages((previous) =>
            previous.filter((_, itemIndex) => itemIndex !== index)
        );

        setGalleryPreviews((previous) =>
            previous.filter((_, itemIndex) => itemIndex !== index)
        );

        setForm((previous) => ({
            ...previous,
            gallery_images: previous.gallery_images.filter(
                (_, itemIndex) => itemIndex !== index
            ),
        }));
    };

    /* =========================================================
       STORAGE
    ========================================================= */

    const uploadImage = async (file, folder) => {
        const fileName = createUniqueFileName(file);

        const filePath = `${folder}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from("activities")
            .upload(filePath, file, {
                cacheControl: "3600",
                upsert: false,
                contentType: file.type,
            });

        if (uploadError) {
            console.error("Image upload error:", uploadError);
            throw uploadError;
        }

        const {
            data: { publicUrl },
        } = supabase.storage
            .from("activities")
            .getPublicUrl(filePath);

        return {
            url: publicUrl,
            path: filePath,
        };
    };

    const deleteStorageImage = async (url) => {
        const path = getStoragePathFromUrl(url);

        if (!path) {
            console.warn(
                "Could not determine Storage path from URL:",
                url
            );
            return false;
        }

        const { data, error: deleteError } = await supabase.storage
            .from("activities")
            .remove([path]);

        if (deleteError) {
            console.error(
                "Error deleting Storage image:",
                deleteError
            );
            return false;
        }

        console.log("Deleted Storage image:", path, data);

        return true;
    };

    /* =========================================================
       SAVE ACTIVITY
    ========================================================= */

    const handleSubmit = async (event) => {
        event.preventDefault();

        setSaving(true);
        setError("");
        setSuccess("");

        let uploadedCoverUrl = null;
        const uploadedGalleryUrls = [];

        try {
            /* -------------------------------------------------------
               VALIDATION
            ------------------------------------------------------- */

            if (!form.title.trim()) {
                throw new Error("Activity title is required.");
            }

            if (!form.slug.trim()) {
                throw new Error("Activity slug is required.");
            }

            /* -------------------------------------------------------
               COVER IMAGE
            ------------------------------------------------------- */

            let newCoverImage = form.cover_image || "";

            if (selectedCoverImage) {
                const uploadedCover = await uploadImage(
                    selectedCoverImage,
                    "covers"
                );

                uploadedCoverUrl = uploadedCover.url;
                newCoverImage = uploadedCover.url;
            }

            /* -------------------------------------------------------
               GALLERY IMAGES
            ------------------------------------------------------- */

            /*
              galleryPreviews contains:
      
              Existing image URLs
              +
              Object URLs for newly selected files
      
              We only upload files from selectedGalleryImages.
            */

            let finalGalleryImages = [...form.gallery_images];

            if (selectedGalleryImages.length > 0) {
                const galleryFolder = isEditMode
                    ? `gallery/${id}`
                    : `gallery/temp-${Date.now()}`;

                for (const file of selectedGalleryImages) {
                    const uploadedGallery = await uploadImage(
                        file,
                        galleryFolder
                    );

                    uploadedGalleryUrls.push(uploadedGallery.url);

                    finalGalleryImages.push(uploadedGallery.url);
                }
            }

            /* -------------------------------------------------------
               REMOVE DUPLICATES
            ------------------------------------------------------- */

            finalGalleryImages = [
                ...new Set(finalGalleryImages.filter(Boolean)),
            ];

            /* -------------------------------------------------------
               INSERT / UPDATE
            ------------------------------------------------------- */

            const payload = {
                title: form.title.trim(),
                slug: form.slug.trim(),
                category: form.category.trim() || null,
                activity_date: form.activity_date || null,
                location: form.location.trim() || null,
                description: form.description.trim() || null,
                cover_image: newCoverImage || null,
                gallery_images: finalGalleryImages,
                published: form.published,
                featured: form.featured,
                updated_at: new Date().toISOString(),
            };



            if (isEditMode) {
                const { data, error: updateError } = await supabase
                    .from("activities")
                    .update(payload)
                    .eq("id", id)
                    .select()
                    .single();

                if (updateError) {
                    throw updateError;
                }

                if (!data) {
                    throw new Error(
                        "Activity update did not return any data."
                    );
                }
            } else {
                const { data, error: insertError } = await supabase
                    .from("activities")
                    .insert(payload)
                    .select()
                    .single();

                if (insertError) {
                    throw insertError;
                }

                if (!data) {
                    throw new Error(
                        "Activity creation did not return any data."
                    );
                }
            }

            /* -------------------------------------------------------
               DELETE REPLACED COVER IMAGE
            ------------------------------------------------------- */

            if (
                isEditMode &&
                originalCoverImage &&
                originalCoverImage !== newCoverImage
            ) {
                const deleted = await deleteStorageImage(
                    originalCoverImage
                );

                if (!deleted) {
                    console.warn(
                        "Old cover image could not be deleted."
                    );
                }
            }

            /* -------------------------------------------------------
               DELETE REMOVED GALLERY IMAGES
            ------------------------------------------------------- */

            if (isEditMode) {
                const removedGalleryImages =
                    originalGalleryImages.filter(
                        (oldImage) =>
                            !finalGalleryImages.includes(oldImage)
                    );

                for (const oldImage of removedGalleryImages) {
                    const deleted = await deleteStorageImage(oldImage);

                    if (!deleted) {
                        console.warn(
                            "Old gallery image could not be deleted:",
                            oldImage
                        );
                    }
                }
            }

            /* -------------------------------------------------------
               UPDATE LOCAL STATE
            ------------------------------------------------------- */

            setForm((previous) => ({
                ...previous,
                cover_image: newCoverImage,
                gallery_images: finalGalleryImages,
            }));

            setOriginalCoverImage(newCoverImage);
            setOriginalGalleryImages(finalGalleryImages);

            setSelectedCoverImage(null);
            setSelectedGalleryImages([]);

            setSuccess(
                isEditMode
                    ? "Activity updated successfully."
                    : "Activity created successfully."
            );

            /*
              For a new activity, wait briefly so the user can see
              the success message before going back.
            */

            if (!isEditMode) {
                setTimeout(() => {
                    navigate("/admin/activities");
                }, 700);
            }
        } catch (submitError) {
            console.error(
                "Error saving activity:",
                submitError
            );

            /*
              If the database save failed after images were uploaded,
              clean up those newly uploaded files.
            */

            if (uploadedCoverUrl) {
                await deleteStorageImage(uploadedCoverUrl);
            }

            for (const uploadedGalleryUrl of uploadedGalleryUrls) {
                await deleteStorageImage(uploadedGalleryUrl);
            }

            setError(
                submitError?.message ||
                "Something went wrong while saving the activity."
            );
        } finally {
            setSaving(false);
        }
    };

    /* =========================================================
       CANCEL
    ========================================================= */

    const handleCancel = () => {
        navigate("/admin/activities");
    };

    /* =========================================================
       LOADING
    ========================================================= */

    if (loading) {
        return (
            <div className="admin-activity-editor">
                <AdminHeader
                    backTo="/admin/activities"
                    backLabel="Back to Activities"
                />

                <main className="admin-activity-editor-container">
                    <div className="admin-activity-editor-loading">
                        Loading activity...
                    </div>
                </main>
            </div>
        );
    }

    /* =========================================================
       UI
    ========================================================= */

    return (
        <div className="admin-activity-editor">
            <AdminHeader
                backTo="/admin/activities"
                backLabel="Back to Activities"
            />

            <main className="admin-activity-editor-container">
                <div className="admin-activity-editor-heading">
                    <div>
                        <span className="admin-activity-editor-eyebrow">
                            ACTIVITIES
                        </span>

                        <h1>
                            {isEditMode
                                ? "Edit Activity"
                                : "Create Activity"}
                        </h1>

                        <p>
                            {isEditMode
                                ? "Update the activity details, images and visibility."
                                : "Add an activity to the Sudhamshu Ayurveda website."}
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="admin-activity-editor-alert error">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="admin-activity-editor-alert success">
                        {success}
                    </div>
                )}

                <form
                    className="admin-activity-editor-form"
                    onSubmit={handleSubmit}
                >
                    {/* =================================================
              BASIC INFORMATION
          ================================================= */}

                    <section className="admin-activity-editor-card">
                        <div className="admin-activity-editor-card-heading">
                            <h2>Activity Information</h2>
                            <p>
                                Basic information about the activity.
                            </p>
                        </div>

                        <div className="admin-activity-editor-grid">
                            <div className="admin-activity-field full">
                                <label htmlFor="title">
                                    Title <span>*</span>
                                </label>

                                <input
                                    id="title"
                                    name="title"
                                    type="text"
                                    value={form.title}
                                    onChange={handleTitleChange}
                                    placeholder="Example: Children's Wellness Camp"
                                    required
                                />
                            </div>

                            <div className="admin-activity-field">
                                <label htmlFor="slug">
                                    Slug <span>*</span>
                                </label>

                                <input
                                    id="slug"
                                    name="slug"
                                    type="text"
                                    value={form.slug}
                                    onChange={handleChange}
                                    placeholder="childrens-wellness-camp"
                                    required
                                />

                                <small>
                                    Used in the activity URL.
                                </small>
                            </div>

                            <div className="admin-activity-field">
                                <label htmlFor="category">
                                    Category
                                </label>

                                <input
                                    id="category"
                                    name="category"
                                    type="text"
                                    value={form.category}
                                    onChange={handleChange}
                                    placeholder="Community Wellness"
                                />
                            </div>

                            <div className="admin-activity-field">
                                <label htmlFor="activity_date">
                                    Activity Date
                                </label>

                                <input
                                    id="activity_date"
                                    name="activity_date"
                                    type="date"
                                    value={form.activity_date}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="admin-activity-field">
                                <label htmlFor="location">
                                    Location
                                </label>

                                <input
                                    id="location"
                                    name="location"
                                    type="text"
                                    value={form.location}
                                    onChange={handleChange}
                                    placeholder="Bengaluru"
                                />
                            </div>

                            <div className="admin-activity-field full">
                                <label htmlFor="description">
                                    Description
                                </label>

                                <textarea
                                    id="description"
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    rows={7}
                                    placeholder="Describe the activity..."
                                />
                            </div>
                        </div>
                    </section>

                    {/* =================================================
              COVER IMAGE
          ================================================= */}

                    <section className="admin-activity-editor-card">
                        <div className="admin-activity-editor-card-heading">
                            <h2>Cover Image</h2>

                            <p>
                                This image represents the activity across
                                the website.
                            </p>
                        </div>

                        <div className="admin-activity-cover-upload">
                            {coverPreview ? (
                                <div className="admin-activity-cover-preview">
                                    <img
                                        src={coverPreview}
                                        alt="Activity cover preview"
                                    />

                                    <button
                                        type="button"
                                        className="admin-activity-image-remove"
                                        onClick={handleRemoveCoverImage}
                                    >
                                        Remove Image
                                    </button>
                                </div>
                            ) : (
                                <label
                                    htmlFor="cover-image"
                                    className="admin-activity-upload-box"
                                >
                                    <span className="admin-activity-upload-icon">
                                        +
                                    </span>

                                    <strong>
                                        Upload Cover Image
                                    </strong>

                                    <small>
                                        JPG, PNG or WebP · Maximum 5 MB
                                    </small>
                                </label>
                            )}

                            <input
                                id="cover-image"
                                type="file"
                                accept="image/*"
                                onChange={handleCoverImageChange}
                                hidden
                            />

                            {coverPreview && (
                                <label
                                    htmlFor="cover-image"
                                    className="admin-activity-change-image"
                                >
                                    Change Cover Image
                                </label>
                            )}
                        </div>
                    </section>

                    {/* =================================================
              GALLERY
          ================================================= */}

                    <section className="admin-activity-editor-card">
                        <div className="admin-activity-editor-card-heading">
                            <h2>Gallery</h2>

                            <p>
                                Add additional images from the activity.
                            </p>
                        </div>

                        <div className="admin-activity-gallery">
                            {galleryPreviews.length > 0 && (
                                <div className="admin-activity-gallery-grid">
                                    {galleryPreviews.map((image, index) => (
                                        <div
                                            className="admin-activity-gallery-item"
                                            key={`${image}-${index}`}
                                        >
                                            <img
                                                src={image}
                                                alt={`Gallery ${index + 1}`}
                                            />

                                            <button
                                                type="button"
                                                className="admin-activity-gallery-remove"
                                                onClick={() =>
                                                    handleRemoveGalleryImage(index)
                                                }
                                                aria-label={`Remove gallery image ${index + 1
                                                    }`}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <label
                                htmlFor="gallery-images"
                                className="admin-activity-gallery-add"
                            >
                                <span>+</span>
                                <strong>Add Gallery Images</strong>
                                <small>
                                    You can select multiple images · Maximum 5 MB
                                    each
                                </small>
                            </label>

                            <input
                                id="gallery-images"
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleGalleryImageChange}
                                hidden
                            />
                        </div>
                    </section>

                    {/* =================================================
              VISIBILITY
          ================================================= */}

                    <section className="admin-activity-editor-card">
                        <div className="admin-activity-editor-card-heading">
                            <h2>Visibility</h2>

                            <p>
                                Control where this activity appears.
                            </p>
                        </div>

                        <div className="admin-activity-options">
                            <label className="admin-activity-checkbox">
                                <input
                                    type="checkbox"
                                    name="published"
                                    checked={form.published}
                                    onChange={handleChange}
                                />

                                <span>
                                    <strong>Published</strong>
                                    <small>
                                        Make this activity visible on the
                                        public website.
                                    </small>
                                </span>
                            </label>

                            <label className="admin-activity-checkbox">
                                <input
                                    type="checkbox"
                                    name="featured"
                                    checked={form.featured}
                                    onChange={handleChange}
                                />

                                <span>
                                    <strong>Featured</strong>
                                    <small>
                                        Allow this activity to appear in the
                                        homepage Activities section.
                                    </small>
                                </span>
                            </label>
                        </div>
                    </section>

                    {/* =================================================
              ACTIONS
          ================================================= */}

                    <div className="admin-activity-editor-actions">
                        <button
                            type="button"
                            className="admin-activity-cancel"
                            onClick={handleCancel}
                            disabled={saving}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="admin-activity-save"
                            disabled={saving}
                        >
                            {saving
                                ? "Saving..."
                                : isEditMode
                                    ? "Update Activity"
                                    : "Create Activity"}
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );
};

export default AdminActivityEditor;